import React, { useEffect, useRef, useState, useCallback } from "react";
import { I } from "../data";
import { http } from "../api";
import { useAuth } from "../AuthContext";
import { useWorkspace } from "../WorkspaceContext";
import { useUpgrade, FEATURES } from "../UpgradeContext";
import { useCopilot } from "../CopilotContext";
import { streamChat } from "../lib/aiEngine";
import { AiMessage, UserMessage } from "./ChatMessages";
import { StepReveal, toolStepsForKind } from "./StepReveal";
import { VidhyaMark } from "./VidhyaMark";

// Tools no longer live on the landing page — they run in the background, triggered
// either by buttons across the app (uni page, tasks, board) or by the AI mid-chat.
const ADV_TOOLS = ["suggestUnis", "essay", "appPlan", "advice", "todo"];
const TOOL_TITLE = {
  suggestUnis: "Universities that fit you", essay: "Essay strategy", email: "Email draft",
  fit: "Fit & aid", browse: "Browsing live", todo: "What to do next",
  appPlan: "Application plan", advice: "Honest read · your list",
};
const TOOL_ICON = {
  suggestUnis: "GraduationCap", essay: "Doc", email: "Mail", fit: "Compare", browse: "Globe",
  todo: "Check", appPlan: "Calendar", advice: "Chat",
};
const TOOL_INTRO = {
  suggestUnis: "Here are some schools that fit your profile — say the word and I'll add any to your board.",
  essay: "Let's crack it — here's what the prompt is really asking and a structure you fill in your own words.",
  appPlan: "Here's an ordered plan to your deadline.",
  advice: "Here's my honest read of your whole list — strengths and gaps, no hype.",
  todo: "Here's what I'd prioritise across your schools right now.",
  email: "Here's a draft you can tweak — personalised from your profile.",
  fit: "Here's your fit, admit chance and funding gap.",
  browse: "Pulling this from official sources right now — nothing from memory.",
};
let _k = 0;
const nextKey = () => `c-${Date.now()}-${_k++}`;

export default function CopilotDrawer({ go }) {
  const { open, mode, ctx, actionNonce, width, closeCopilot, setWidth, consumePreset, consumeInputPreset } = useCopilot();
  const { user } = useAuth();
  const ws = useWorkspace();
  const { universities, addUniversity, addTask, profile, gmail, refresh } = ws;
  const { open: openUpgrade } = useUpgrade();

  const [messages, setMessages] = useState([]);
  const [composing, setComposing] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [threads, setThreads] = useState([]);
  const bodyRef = useRef(null);
  const taRef = useRef(null);
  const handledNonceRef = useRef(0);
  const justDraggedRef = useRef(false);
  const nearBottomRef = useRef(true);
  const threadIdRef = useRef(null);
  const creatingRef = useRef(false);
  const msgsRef = useRef(messages);
  msgsRef.current = messages;

  const navTo = useCallback((v, payload) => { closeCopilot(); if (go) go(v, payload); }, [closeCopilot, go]);

  const patch = (key, p) => setMessages((prev) => {
    const c = [...prev]; const i = c.findIndex((m) => m._k === key);
    if (i >= 0) c[i] = typeof p === "function" ? p(c[i]) : { ...c[i], ...p }; return c;
  });

  // Pull the user's saved chats from the server so History syncs across devices.
  const refreshThreads = useCallback(async () => {
    try { const { data } = await http.get("/chats"); setThreads(data.items || []); } catch (e) { console.warn("copilot: failed to load chat threads", e); }
  }, []);
  useEffect(() => { refreshThreads(); }, [refreshThreads]);

  // Persist the active thread to the server (debounced) so history reflects
  // reality without writing on every streaming token.
  useEffect(() => {
    if (messages.length === 0) return;
    const t = setTimeout(async () => {
      const title = ((messages.find((m) => m.role === "user")?.text)
        || TOOL_TITLE[messages.find((m) => m.role === "tool")?.kind] || "New chat").slice(0, 70);
      const payload = { title, messages };
      try {
        if (threadIdRef.current) {
          await http.patch(`/chats/${threadIdRef.current}`, payload);
        } else if (!creatingRef.current) {
          creatingRef.current = true;
          const { data } = await http.post("/chats", payload);
          threadIdRef.current = data.id;
          creatingRef.current = false;
        }
        refreshThreads();
      } catch (e) { creatingRef.current = false; }
    }, 700);
    return () => clearTimeout(t);
  }, [messages, refreshThreads]);

  // ── Normal chat turn (AI decides everything, may use tools rendered inline) ──
  const doSend = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    setBusy(true); nearBottomRef.current = true;
    const history8 = msgsRef.current.filter((m) => (m.text || "").trim()).slice(-8)
      .map((m) => ({ role: m.role === "ai" ? "assistant" : "user", text: m.text }));
    setMessages((prev) => [...prev, { _k: nextKey(), role: "user", text: msg }]);
    const aiKey = nextKey();
    setMessages((prev) => [...prev, { _k: aiKey, role: "ai", text: "", tools: [], intentText: msg }]);
    try {
      const { tools } = await streamChat({
        message: msg, history: history8,
        callbacks: {
          onDelta: (full) => patch(aiKey, { text: full }),
          onTools: (tools) => patch(aiKey, { tools }),
          onDone: (final) => patch(aiKey, { text: final }),
          onBlocked: (s, d) => { patch(aiKey, { text: d?.message || "You've used your free advisor messages for today." }); openUpgrade(d?.feature || FEATURES.AI_ADVISOR, d?.context || "copilot_quota"); },
        },
      });
      // Tools ran SERVER-SIDE; if any added a university or task, sync the board.
      if ((tools || []).some((t) => t.result?.kind === "shortlisted" || t.result?.kind === "task")) {
        refresh();
      }
    } catch (e) { patch(aiKey, { text: `Sorry, I hit an error: ${e?.message || "please try again."}` }); }
    finally { setBusy(false); }
  }, [busy, openUpgrade, refresh]);

  // ── Contextual tool run (from app buttons / AI) → renders bespoke UI INLINE ──
  const runAction = useCallback(async (m, c) => {
    const introKey = nextKey(); const toolKey = nextKey();
    nearBottomRef.current = true;
    setMessages((prev) => [...prev,
      { _k: introKey, role: "ai", text: TOOL_INTRO[m] || "" },
      { _k: toolKey, role: "tool", kind: m, status: "loading", ctx: c },
    ]);
    setBusy(true);
    try {
      if (ADV_TOOLS.includes(m)) {
        const { data } = await http.post("/ai/advisor-tool", { tool: m, context: c || {} });
        patch(toolKey, { status: "done", data: data.data || {} });
      } else if (m === "email") {
        const bg = [profile?.intended_major ? `${profile.intended_major} applicant` : "applicant",
          profile?.student_country ? `from ${profile.student_country}` : ""].filter(Boolean).join(" ");
        const { data } = await http.post("/ai/draft-email", {
          professor_name: c?.professor || "the admissions office", professor_email: c?.email || "",
          professor_research: c?.research || "", university: c?.uni || "",
          student_name: user?.name || "Student", student_background: bg || "international applicant",
          program_applying: c?.program || profile?.intended_major || "", purpose: c?.professor ? "research_inquiry" : "general_inquiry",
        });
        patch(toolKey, { status: "done", data: data.data || {}, uni: c?.uni, to: c?.email, professor: c?.professor, uniId: c?.uniId });
      } else if (m === "fit") {
        const uid = c?.uniId; const uName = c?.uni;
        if (!uid) { patch(toolKey, { status: "error", err: "Open a university first so I know which one to check." }); return; }
        const { data } = await http.post(`/universities/${uid}/cost-and-aid`);
        patch(toolKey, { status: "done", data: data.data || {}, uni: uName, uid });
      } else if (m === "browse") {
        const uName = c?.uni; if (!uName) { patch(toolKey, { status: "error", err: "Tell me which university to check live." }); return; }
        const { data } = await http.post("/ai/research-university", { university_name: uName, program: c?.program || profile?.intended_major || "" });
        patch(toolKey, { status: "done", data: data.data || {}, uni: uName });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msgTxt = (typeof detail === "object" ? detail?.message : detail) || e?.message || "Couldn't load this just now.";
      if (e?.response?.status === 402) { patch(toolKey, { status: "gated", err: msgTxt }); openUpgrade(detail?.feature || FEATURES.AI_ADVISOR, detail?.context || `copilot_${m}`); }
      else patch(toolKey, { status: "error", err: msgTxt });
    } finally { setBusy(false); }
  }, [profile, user, openUpgrade]);

  // Run a contextual action exactly once per openCopilot(non-menu) call.
  // Also auto-sends a preset prompt (e.g. "Research more" from a uni page) once per nonce.
  useEffect(() => {
    if (!open) return;
    if (handledNonceRef.current === actionNonce) return;
    handledNonceRef.current = actionNonce;
    const preset = consumePreset();
    if (preset && (preset || "").trim()) {
      doSend(preset);
      return;
    }
    // Pre-load the composer buffer (e.g. "Work with Vidhya" from a Task row) so the
    // user can review/edit the seeded prompt before sending — does NOT auto-send.
    const inputPreset = consumeInputPreset();
    if (inputPreset && (inputPreset || "").trim()) {
      setComposing(inputPreset);
      setTimeout(() => { if (taRef.current) { taRef.current.focus(); taRef.current.setSelectionRange(inputPreset.length, inputPreset.length); } }, 60);
      return;
    }
    if (mode !== "menu") runAction(mode, ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actionNonce]);

  useEffect(() => { if (bodyRef.current && nearBottomRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages]);
  const onBodyScroll = () => { const el = bodyRef.current; if (el) nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; };

  const startResize = useCallback((e) => {
    e.preventDefault(); setDragging(true);
    const move = (ev) => { const cx = ev.touches ? ev.touches[0].clientX : ev.clientX; setWidth(window.innerWidth - cx); };
    const up = () => { setDragging(false); justDraggedRef.current = true; setTimeout(() => { justDraggedRef.current = false; }, 250);
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move); window.addEventListener("touchend", up);
  }, [setWidth]);

  const newChat = () => { threadIdRef.current = null; creatingRef.current = false; setMessages([]); setShowHistory(false); };
  const openThread = async (t) => {
    try { const { data } = await http.get(`/chats/${t.id}`); threadIdRef.current = t.id; setMessages(data.messages || []); }
    catch (e) { console.warn("copilot: failed to open thread", e); } finally { setShowHistory(false); }
  };
  const clearHistory = async () => {
    const ids = threads.map((t) => t.id);
    setThreads([]);
    if (ids.includes(threadIdRef.current)) { threadIdRef.current = null; setMessages([]); }
    await Promise.allSettled(ids.map((id) => http.delete(`/chats/${id}`)));
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isLanding = messages.length === 0 && !showHistory;

  const submit = () => { const t = composing.trim(); if (!t) return; setComposing(""); if (taRef.current) taRef.current.style.height = "auto"; doSend(t); };
  const onTaInput = (e) => { setComposing(e.target.value); const el = e.target; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 132) + "px"; };

  return (
    <>
      <div className={`cop-scrim ${open ? "show" : ""}`} onClick={closeCopilot} data-testid="copilot-scrim" />
      <aside className={`cop ${open ? "show" : ""} ${dragging ? "dragging" : ""}`} style={{ width }} data-testid="copilot-drawer" aria-label="Vidhya copilot">
        <div className="cop-resize" onMouseDown={startResize} onTouchStart={startResize} data-testid="copilot-resize" />

        <div className="coph">
          <div className="ci"><VidhyaMark size={17} /></div>
          <div className="ct"><b>Vidhya</b><span className="ctx"><span className="dot" />Your whole plan</span></div>
          <div className="coph-actions">
            <button className="hb" onClick={newChat} title="New chat" data-testid="copilot-new-chat"><I.Plus size={14} /></button>
            <button className={`hb ${showHistory ? "on" : ""}`} onClick={() => setShowHistory((s) => { const n = !s; if (n) refreshThreads(); return n; })} title="History" data-testid="copilot-history-btn"><I.Inbox size={14} /></button>
            <button className="hb x" onClick={closeCopilot} aria-label="Close" data-testid="copilot-close"><I.Close size={14} /></button>
          </div>
        </div>

        <div className="copb" ref={bodyRef} onScroll={onBodyScroll} data-testid="copilot-body">
          {showHistory ? (
            <HistoryView threads={threads} onOpen={openThread} onClear={clearHistory} />
          ) : isLanding ? (
            <LandingView user={user} universities={universities} onAsk={(q) => doSend(q)} />
          ) : (
            messages.map((m, i) => {
              if (m.role === "user") return <UserMessage key={m._k} text={m.text} initials={initials} />;
              if (m.role === "tool") return <ToolMessage key={m._k} m={m} go={navTo} onAddUni={addUniversity} onAddTask={addTask} gmail={gmail} />;
              const isActiveStream = busy && i === messages.length - 1;
              if (!m.text && !(m.tools || []).length && !isActiveStream) return null;
              return <AiMessage key={m._k} msg={m} go={navTo} streaming={isActiveStream} onAddTask={addTask} />;
            })
          )}
        </div>

        <div className="copf">
          <div className="box">
            <textarea ref={taRef} rows={1} placeholder="Ask Vidhya anything…" aria-label="Ask Vidhya" value={composing}
              onChange={onTaInput} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              data-testid="copilot-input" disabled={busy} />
            <button className="snd" aria-label="Send" onClick={submit} disabled={busy || !composing.trim()} data-testid="copilot-send"><I.ArrowRight size={15} /></button>
          </div>
        </div>
      </aside>
    </>
  );
}

function LandingView({ user, universities, onAsk }) {
  const first = (user?.name || "there").split(" ")[0];
  const topUni = universities[0]?.name;
  const sugg = [
    { icon: "Check", q: "What should I focus on this week?" },
    topUni ? { icon: "Compare", q: `How are my chances at ${topUni}?` } : { icon: "GraduationCap", q: "Suggest universities that fit my profile" },
    { icon: "Coin", q: "Find scholarships I'm eligible for" },
    { icon: "Chat", q: "Give me an honest read of my shortlist" },
  ];
  return (
    <div className="cop-landing" data-testid="copilot-landing">
      <div className="cl-hero">
        <div className="cl-mark"><VidhyaMark size={22} /></div>
        <h3>Hi {first} — ask me anything.</h3>
        <p>I can see all {universities.length || "your"} {universities.length === 1 ? "university" : universities.length ? "universities" : "plan"}, your deadlines and your profile. I'll research live, draft outreach, find funding and keep you on track.</p>
      </div>
      <div className="cl-sugg-label">Try asking</div>
      {sugg.map((s, i) => { const Icon = I[s.icon]; return (
        <button key={i} className="cl-sugg" onClick={() => onAsk(s.q)} data-testid={`copilot-suggestion-${i}`}>
          <span className="ico">{Icon ? <Icon size={15} /> : null}</span><span>{s.q}</span><I.ArrowRight size={14} />
        </button>
      ); })}
    </div>
  );
}

function HistoryView({ threads, onOpen, onClear }) {
  return (
    <div data-testid="copilot-history">
      <div className="cop-hist-head"><b>Recent chats</b>{threads.length > 0 && <button onClick={onClear} data-testid="copilot-history-clear">Clear</button>}</div>
      {threads.length === 0 ? (
        <p className="copintro">No chats yet. Ask something and it'll show up here so you can pick up where you left off.</p>
      ) : threads.map((t) => (
        <button key={t.id} className="cop-hist-item" onClick={() => onOpen(t)} data-testid="copilot-history-item">
          <b>{t.title}</b><span>{new Date(t.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {new Date(t.updated_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
        </button>
      ))}
    </div>
  );
}

// ── Inline tool message: renders the bespoke per-tool UI inside the chat stream ──
function ToolMessage({ m, go, onAddUni, onAddTask, gmail }) {
  const { kind, status, data = {}, err } = m;
  const Icon = I[TOOL_ICON[kind]] || I.Sparkle;
  return (
    <div className="cop-tool-msg" data-testid={`copilot-toolmsg-${kind}`}>
      <div className="cop-tool-head"><span className="tm-ico"><Icon size={13} /></span>{TOOL_TITLE[kind] || "Vidhya"}</div>
      {status === "loading" && (
        <div className="cop-loading" data-testid="copilot-loading">
          <StepReveal steps={toolStepsForKind(kind)} active interval={2400} testid={`tool-steps-${kind}`} />
        </div>
      )}
      {(status === "error" || status === "gated") && <div className="rline" style={{ color: "var(--urgent)" }}>{err}</div>}
      {status === "done" && (
        <>
          {kind === "suggestUnis" && <SuggestUnisView data={data} onAddUni={onAddUni} />}
          {kind === "essay" && <EssayView data={data} />}
          {kind === "appPlan" && <AppPlanView data={data} />}
          {kind === "advice" && <AdviceView data={data} />}
          {kind === "todo" && <TodoView data={data} onAddTask={onAddTask} go={go} />}
          {kind === "email" && <EmailView data={data} tool={m} gmail={gmail} go={go} />}
          {kind === "fit" && <FitView data={data} uni={m.uni} go={go} />}
          {kind === "browse" && <BrowseView data={data} uni={m.uni} />}
        </>
      )}
    </div>
  );
}

const UL = ({ name }) => <div className="ulogo" style={{ background: "var(--ink)", width: 34, height: 34, fontSize: 13 }}>{(name || "?")[0]}</div>;

function SuggestUnisView({ data, onAddUni }) {
  const [added, setAdded] = useState({});
  const items = data.items || [];
  const add = async (it, i) => { setAdded((p) => ({ ...p, [i]: "adding" }));
    try { await onAddUni({ name: it.name, program: data.program || "", color: "#5645F0", status: "researching" }); setAdded((p) => ({ ...p, [i]: "done" })); }
    catch { setAdded((p) => ({ ...p, [i]: null })); } };
  return (<>
    {data.intro && <div className="rline">{data.intro}</div>}
    {items.map((it, i) => {
      const ambitious = /reach|ambit/i.test(it.tier || "");
      return (
        <div className="rcard" key={i} data-testid="sugg-uni-card">
          <div className="runi"><UL name={it.name} />
            <div className="ri2"><b>{it.name} {it.match != null && <span className="matchg" style={ambitious ? { marginLeft: 4, background: "var(--indigo-50)", color: "var(--indigo)" } : { marginLeft: 4 }}>{it.match}%</span>}</b>
              <span>{[it.country, it.tier].filter(Boolean).join(" · ")}{it.why ? ` · ${it.why}` : ""}</span></div>
            <button className="addmini" disabled={!!added[i]} onClick={() => add(it, i)} data-testid="sugg-uni-add">{added[i] === "done" ? "Added ✓" : added[i] === "adding" ? "…" : "Add"}</button>
          </div>
        </div>
      );
    })}
  </>);
}

function EssayView({ data }) {
  return (<>
    {data.intro && <div className="rline">{data.intro}</div>}
    {(data.asks || []).length > 0 && <><div className="rline"><b>What the prompt is really asking:</b></div>
      <div className="rcard" style={{ fontSize: 12, lineHeight: 1.6 }}>{data.asks.map((a, i) => <div key={i}>{i + 1}. {a}</div>)}</div></>}
    {(data.steps || []).length > 0 && <><div className="rline" style={{ marginTop: 6 }}><b>A structure to fill with your own words:</b></div>
      <div className="rcard">{data.steps.map((s, i) => (<div className="outline-step" key={i}><div className="on">{s.n || i + 1}</div><div><b>{s.title}</b><span>{s.desc}</span></div></div>))}</div></>}
  </>);
}

function AppPlanView({ data }) {
  return (<>{data.intro && <div className="rline">{data.intro}</div>}
    <div className="rcard">{(data.steps || []).map((s, i) => (<div className="outline-step" key={i}><div className="on">{(s.label || "").replace(/[^0-9]/g, "").slice(0, 2) || "•"}</div><div><b>{s.label}</b><span>{s.title}{s.desc ? ` — ${s.desc}` : ""}</span></div></div>))}</div></>);
}

function AdviceView({ data }) {
  const styles = { strong: { background: "var(--verified-tint)", color: "var(--verified)" }, watch: { background: "var(--urgent-tint)", color: "var(--urgent)" }, note: { background: "var(--indigo-50)", color: "var(--indigo)" } };
  return (<>{data.intro && <div className="rline">{data.intro}</div>}
    <div className="rcard" style={{ padding: "4px 13px" }}>{(data.rows || []).map((r, i) => (
      <div key={i} style={{ display: "flex", gap: 10, padding: "9px 0", borderBottom: "1px solid var(--line)", alignItems: "flex-start" }}>
        <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", padding: "3px 8px", borderRadius: 6, flexShrink: 0, marginTop: 1, ...(styles[r.kind] || styles.note) }}>{r.kind}</span>
        <p style={{ fontSize: 12, lineHeight: 1.5 }}>{r.text}</p>
      </div>))}</div>
    {data.net && <div className="rline" style={{ marginTop: 8 }}><b>Net:</b> {data.net}</div>}</>);
}

function TodoView({ data, onAddTask, go }) {
  const [added, setAdded] = useState({});
  const add = async (it, key) => { setAdded((p) => ({ ...p, [key]: "adding" })); try { await onAddTask({ title: it.title, uni: it.uni || "All applications", src: "AI suggested", ai_suggested: true }); setAdded((p) => ({ ...p, [key]: "done" })); } catch { setAdded((p) => ({ ...p, [key]: null })); } };
  return (<>{data.intro && <div className="rline">{data.intro}</div>}
    {(data.groups || []).map((g, gi) => (<div key={gi}><div className="todo-group">{g.label}</div>
      {(g.items || []).map((it, ii) => { const key = `${gi}-${ii}`; return (
        <div className="rcard" key={key} data-testid="todo-card"><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}><b style={{ fontSize: 12 }}>{it.title}</b>{it.uni && <div style={{ marginTop: 3 }}><span className="utag"><span className="d" style={{ background: "var(--ink)" }} />{it.uni}</span></div>}</div>
          <button className="addmini" disabled={!!added[key]} onClick={() => add(it, key)} data-testid="todo-add">{added[key] === "done" ? "Added ✓" : added[key] === "adding" ? "…" : "+ Task"}</button>
        </div></div>); })}</div>))}
    <div className="rbtns"><button className="rbtn fill" onClick={() => go("tasks")} data-testid="todo-open-tasks">Open my Tasks</button></div></>);
}

function EmailView({ data, tool, gmail, go }) {
  return (<>
    <div className="emailprev" data-testid="email-preview">
      <div className="efield"><span className="ek">To</span><span className="ev">{tool.to || tool.professor || "—"}</span></div>
      <div className="efield"><span className="ek">Subj</span><span className="ev">{data.subject || ""}</span></div>
      <div className="emailbody">{data.body || ""}</div>
    </div>
    <div className="rline">Personalised from your profile, one clear ask. {gmail?.connected ? "Sends from your Gmail." : "Connect Gmail to send & track replies."}</div>
    <div className="rbtns">
      {tool.uniId && <button className="rbtn fill" onClick={() => go("detail", tool.uniId)} data-testid="email-open-outreach">Open in Outreach</button>}
      <button className="rbtn gho" onClick={() => navigator.clipboard && navigator.clipboard.writeText(`${data.subject || ""}\n\n${data.body || ""}`)} data-testid="email-copy">Copy</button>
    </div></>);
}

function FitView({ data, uni, go }) {
  const admit = data.admit_chance_pct != null ? Math.max(1, Math.min(99, data.admit_chance_pct)) : null;
  const gap = data.aid_gap_usd; const conf = data.admit_chance_confidence || "medium"; const band = data.admit_chance_band || "moderate";
  return (<>
    {uni && <div className="rline"><b>{uni}</b></div>}
    {admit != null && (<div className="meterbox"><div className="mt2"><span className="mlabel">Admit chance · {conf} confidence</span><span className="mval" style={{ color: "var(--indigo)" }}>{band} · {admit}%</span></div><div className="mtrack"><i style={{ width: `${admit}%`, background: "linear-gradient(90deg,var(--indigo),var(--indigo-deep))" }} /></div></div>)}
    {gap != null && (<div className="meterbox"><div className="mt2"><span className="mlabel">Funding gap</span><span className="mval" style={{ color: gap > 0 ? "var(--urgent)" : "var(--verified)" }}>{gap > 0 ? `$${gap.toLocaleString()}` : "Covered"}</span></div><div className="mtrack"><i style={{ width: gap > 0 ? "60%" : "100%", background: gap > 0 ? "var(--urgent)" : "var(--verified)" }} /></div></div>)}
    {data.admit_chance_reasoning && <div className="rline">{data.admit_chance_reasoning}</div>}
    {(data.caveats || []).slice(0, 2).map((c, i) => <div className="rline" key={i} style={{ color: "var(--slate)" }}>· {c}</div>)}
    <div className="rbtns"><button className="rbtn fill" onClick={() => go("scholarships")} data-testid="fit-find-schol">Find scholarships for the gap</button></div></>);
}

function BrowseView({ data, uni }) {
  const tuition = data.tuition; const deadline = data.deadline;
  return (<>
    <div className="rline">Pulled {uni ? `${uni} ` : ""}facts from official sources — nothing here comes from memory.</div>
    <div className="browse">
      <div className="bl done"><span className="bsp" />Read official admissions page</div>
      <div className="bl done"><span className="bsp" />Checked the latest tuition</div>
      <div className="bl done"><span className="bsp" />Confirmed application deadline</div>
    </div>
    {tuition && <div className="vbadge" style={{ padding: "6px 11px" }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg>Tuition {tuition}{deadline ? ` · Deadline ${deadline}` : ""}</div>}
    {data.official_url && <div className="rline" style={{ marginTop: 8 }}><a href={data.official_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--indigo)", fontWeight: 600 }}>Open official source ↗</a></div>}
  </>);
}
